import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setUnauthorizedHandler, ApiError } from './api.js';
import { Btn, Field, Spinner } from './ui/kit.js';

// The front door. First visit ever: the box's one-time setup code + a strong
// password, then an immediate passkey offer, then notifications — everything
// Niklas must do personally, embedded right here. After that: passkey first,
// password as the fallback.

interface AuthState {
  bootstrapped: boolean;
  signedIn: boolean;
  passkeys: number;
}

const SessionCtx = createContext<{ refresh: () => void }>({ refresh: () => undefined });
export const useSession = () => useContext(SessionCtx);

export function SessionGate(props: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(() => {
    api
      .get<AuthState>('/api/auth/state')
      .then((s) => setState(s))
      .catch(() => setState(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    refresh();
    setUnauthorizedHandler(() => refresh());
  }, [refresh]);

  if (checking && !state) {
    return (
      <div className="ops-firstrun">
        <Spinner />
      </div>
    );
  }
  if (!state) {
    return (
      <div className="ops-firstrun">
        <div className="ops-firstrun__card">
          <h1>Meridian Ops</h1>
          <p className="ops-firstrun__sub">The box is not answering. Try again in a moment.</p>
          <Btn kind="quiet" big onClick={refresh}>
            Try again
          </Btn>
        </div>
      </div>
    );
  }
  if (!state.signedIn) {
    return (
      <SessionCtx.Provider value={{ refresh }}>
        <FirstDoor state={state} onDone={refresh} />
      </SessionCtx.Provider>
    );
  }
  return (
    <SessionCtx.Provider value={{ refresh }}>
      <OnboardingNudges passkeys={state.passkeys} />
      {props.children}
    </SessionCtx.Provider>
  );
}

function FirstDoor(props: { state: AuthState; onDone: () => void }) {
  const [mode, setMode] = useState<'passkey' | 'password' | 'bootstrap'>(
    props.state.bootstrapped ? (props.state.passkeys > 0 ? 'passkey' : 'password') : 'bootstrap',
  );
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passkeySignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const { options, challengeId } = await api.post<{ options: unknown; challengeId: string }>(
        '/api/auth/webauthn/options',
      );
      const cred = await navigator.credentials.get({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        publicKey: decodeOptions(options as any),
      });
      await api.post('/api/auth/webauthn/verify', {
        challengeId,
        response: encodeCredential(cred as PublicKeyCredential),
      });
      props.onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The passkey did not go through. Use your password.');
      setMode('password');
    } finally {
      setBusy(false);
    }
  };

  const passwordSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/password', { password });
      props.onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  const bootstrap = async () => {
    if (password !== confirm) {
      setError('The two passwords differ.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/bootstrap', { code: code.trim(), password });
      props.onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-firstrun">
      <div className="ops-firstrun__card">
        <img className="ops-firstrun__mark" src="/icons/ops-192.png" alt="" />
        <h1>Meridian Ops</h1>
        <p className="ops-firstrun__sub">
          {mode === 'bootstrap'
            ? 'First time on this box. The setup code is in the round ledger.'
            : 'Compose a change. Watch it build itself. Ship it.'}
        </p>

        {mode === 'bootstrap' ? (
          <>
            <Field label="Setup code">
              <input
                className="ops-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                inputMode="text"
                placeholder="from /etc/meridian-ops/bootstrap-code"
              />
            </Field>
            <Field label="Choose the owner password" hint="At least 12 characters. The passkey comes right after.">
              <input
                className="ops-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Once more">
              <input
                className="ops-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Btn kind="primary" big disabled={busy || !code || !password} onClick={() => void bootstrap()}>
              {busy ? <Spinner /> : 'Set up this phone'}
            </Btn>
          </>
        ) : mode === 'passkey' ? (
          <>
            <Btn kind="primary" big disabled={busy} onClick={() => void passkeySignIn()}>
              {busy ? <Spinner /> : 'Sign in with passkey'}
            </Btn>
            <div className="ops-firstrun__alt">
              <button type="button" onClick={() => setMode('password')}>
                Use the password instead
              </button>
            </div>
          </>
        ) : (
          <>
            <Field label="Owner password">
              <input
                className="ops-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                onKeyDown={(e) => e.key === 'Enter' && void passwordSignIn()}
              />
            </Field>
            <Btn kind="primary" big disabled={busy || !password} onClick={() => void passwordSignIn()}>
              {busy ? <Spinner /> : 'Sign in'}
            </Btn>
            {props.state.passkeys > 0 ? (
              <div className="ops-firstrun__alt">
                <button type="button" onClick={() => setMode('passkey')}>
                  Use the passkey
                </button>
              </div>
            ) : null}
          </>
        )}
        {error ? <p className="ops-error">{error}</p> : null}
      </div>
    </div>
  );
}

// After sign-in: offer the passkey once if none exists, then notifications
// once if not yet granted. Quiet, dismissible, never a wall.
function OnboardingNudges(props: { passkeys: number }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('ops-nudge-dismissed') === '1',
  );
  const [busy, setBusy] = useState(false);
  const needsPasskey = props.passkeys === 0 && 'PublicKeyCredential' in window;
  const needsPush = 'Notification' in window && Notification.permission === 'default';
  const { refresh } = useSession();

  if (dismissed || (!needsPasskey && !needsPush)) return null;

  const dismiss = () => {
    sessionStorage.setItem('ops-nudge-dismissed', '1');
    setDismissed(true);
  };

  const addPasskey = async () => {
    setBusy(true);
    try {
      const { options, challengeId } = await api.post<{ options: unknown; challengeId: string }>(
        '/api/auth/webauthn/register/options',
      );
      const cred = await navigator.credentials.create({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        publicKey: decodeCreationOptions(options as any),
      });
      await api.post('/api/auth/webauthn/register/verify', {
        challengeId,
        response: encodeCredential(cred as PublicKeyCredential),
        label: 'Phone',
      });
      refresh();
    } catch {
      // declined — the nudge stays until dismissed
    } finally {
      setBusy(false);
    }
  };

  const allowPush = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        await subscribePush();
        await api.post('/api/push/test');
        dismiss();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-banner ops-banner--update">
      {needsPasskey ? (
        <>
          Add a passkey for one-touch sign-in.
          <button type="button" disabled={busy} onClick={() => void addPasskey()}>
            Add passkey
          </button>
        </>
      ) : (
        <>
          Allow notifications so jobs can reach you.
          <button type="button" disabled={busy} onClick={() => void allowPush()}>
            Allow
          </button>
        </>
      )}
      <button type="button" onClick={dismiss}>
        Later
      </button>
    </div>
  );
}

// Register a new passkey on this device; reused by the first-run nudge and
// by Settings → Security.
export async function registerPasskey(label: string): Promise<void> {
  const { options, challengeId } = await api.post<{ options: unknown; challengeId: string }>(
    '/api/auth/webauthn/register/options',
  );
  const cred = await navigator.credentials.create({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    publicKey: decodeCreationOptions(options as any),
  });
  await api.post('/api/auth/webauthn/register/verify', {
    challengeId,
    response: encodeCredential(cred as PublicKeyCredential),
    label,
  });
}

export async function subscribePush(): Promise<boolean> {
  const reg = await navigator.serviceWorker?.ready;
  if (!reg) return false;
  const { key } = await api.get<{ key: string }>('/api/push/key');
  if (!key) return false;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: b64ToBytes(key).buffer as ArrayBuffer,
  });
  await api.post('/api/push/subscribe', { ...sub.toJSON(), deviceLabel: navigator.userAgent.includes('Android') ? 'Android phone' : 'Phone' });
  return true;
}

// ── base64url plumbing for WebAuthn ─────────────────────────────────────────
function b64ToBytes(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}
function bytesToB64(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decodeOptions(options: any): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: b64ToBytes(options.challenge),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64ToBytes(c.id),
    })),
  };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decodeCreationOptions(options: any): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: b64ToBytes(options.challenge),
    user: { ...options.user, id: b64ToBytes(options.user.id) },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64ToBytes(c.id),
    })),
  };
}
function encodeCredential(cred: PublicKeyCredential) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = cred.response as any;
  return {
    id: cred.id,
    rawId: bytesToB64(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64(r.clientDataJSON),
      ...(r.attestationObject ? { attestationObject: bytesToB64(r.attestationObject) } : {}),
      ...(r.authenticatorData ? { authenticatorData: bytesToB64(r.authenticatorData) } : {}),
      ...(r.signature ? { signature: bytesToB64(r.signature) } : {}),
      ...(r.userHandle ? { userHandle: bytesToB64(r.userHandle) } : {}),
      ...(typeof r.getTransports === 'function' ? { transports: r.getTransports() } : {}),
    },
  };
}
